import { useEffect, useRef, useState, useMemo } from 'react';
import { ExternalLink, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import PartnerBanner from './PartnerBanner';
import useJsonLd from '../../hooks/useJsonLd';

const API = process.env.REACT_APP_BACKEND_URL;
const SITE = 'https://buildpilot.com.au';

/**
 * CompleteYourProject
 * ─────────────────────────────────────────────────────────────────────
 * Strategic, project-first supplier suggestions. Pass the project type
 * (and optionally the stage) and it shows only the needs we can genuinely
 * serve, filtered to that project. Renders NOTHING when there is no
 * coverage, so it never feels like an ad or a shop.
 *
 * Multi-supplier by design: a single need can pull products from several
 * Suppliers. When one Supplier covers the need we say "From X"; when several
 * do, each product carries its Supplier and we link out to each. Suppliers
 * are never called "Partners". Purchases happen on the retailer's site via
 * tracked links. variant='compact' shows a single subtle block.
 */
const money = (v) => {
  if (!v) return '';
  const n = parseFloat(String(v).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? `$${n.toLocaleString('en-AU', { maximumFractionDigits: 0 })}` : '';
};

const NeedRow = ({ need, dense = false }) => {
  const scrollerRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);
  const suppliers = need.suppliers || (need.supplier ? [need.supplier] : []);
  const single = suppliers.length === 1 ? suppliers[0] : null;
  const multi = suppliers.length > 1;
  const nudge = (dir) => {
    const el = scrollerRef.current;
    if (el) el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.9), behavior: 'smooth' });
  };
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      setAtStart(el.scrollLeft <= 2);
      setAtEnd(el.scrollLeft >= max - 2);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => { el.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  }, [need.products]);
  const overflowing = !(atStart && atEnd);
  const cardW = dense ? 'w-[128px] sm:w-[146px]' : 'w-[190px] sm:w-[210px]';
  const arrowBase = 'w-8 h-8 inline-flex items-center justify-center rounded-full ring-1 ring-black/10 bg-white text-[#1d1d1f] transition-colors';
  return (
    <div className={dense ? 'mb-0' : 'mb-9 last:mb-0'} data-testid={`cyp-need-${need.need}`}>
      <div className={`flex items-end justify-between gap-3 ${dense ? 'mb-2.5' : 'mb-3'}`}>
        <div>
          {!dense && (
            <h3 className="text-[18px] sm:text-[20px] font-bold text-[#1d1d1f] tracking-[-0.01em] leading-tight">
              {need.label}
            </h3>
          )}
          {single && single.slug && (
            <p className={`text-[12.5px] text-[#6b7078] ${dense ? '' : 'mt-1'}`}>
              From{' '}
              <Link to={`/suppliers/${single.slug}`} className="font-semibold text-[#1d1d1f] hover:text-[#1d8efe] transition-colors" data-testid={`cyp-supplier-${need.need}`}>
                {single.name}
              </Link>
            </p>
          )}
          {multi && <p className={`text-[12.5px] text-[#6b7078] ${dense ? '' : 'mt-1'}`}>Suggested by BuildPilot suppliers</p>}
        </div>
        {overflowing && (
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => nudge(-1)}
              disabled={atStart}
              aria-label="Previous products"
              data-testid={`cyp-prev-${need.need}`}
              className={`${arrowBase} ${atStart ? 'opacity-30 cursor-not-allowed' : 'hover:bg-[#1d1d1f] hover:text-white'}`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => nudge(1)}
              disabled={atEnd}
              aria-label="Next products"
              data-testid={`cyp-next-${need.need}`}
              className={`${arrowBase} ${atEnd ? 'opacity-30 cursor-not-allowed' : 'hover:bg-[#1d1d1f] hover:text-white'}`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      <div
        ref={scrollerRef}
        className="flex gap-2.5 sm:gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth -mx-5 px-5 sm:mx-0 sm:px-0 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {need.products.map((p) => (
          <a
            key={p.id}
            href={`${API}${p.go_url}`}
            target="_blank"
            rel="sponsored nofollow noopener noreferrer"
            className={`group flex-none ${cardW} snap-start bg-white border border-gray-200 rounded-xl overflow-hidden hover:-translate-y-0.5 hover:shadow-lg hover:border-[#1d8efe]/40 transition-all duration-300`}
            data-testid={`cyp-product-${p.id}`}
          >
            <div className="aspect-square bg-[#f6f7f9] overflow-hidden relative">
              {p.save > 0 && (
                <span className={`absolute z-10 bg-[#e5322d] text-white font-bold rounded shadow-sm ${dense ? 'top-1.5 left-1.5 text-[9px] px-1.5 py-0.5' : 'top-2 left-2 text-[10.5px] px-1.5 py-0.5 rounded-md'}`}>
                  Save ${p.save}
                </span>
              )}
              {p.image && (
                <img src={p.image} alt={p.name} loading="lazy" decoding="async" className={`w-full h-full object-contain group-hover:scale-105 transition-transform duration-300 ${dense ? 'p-2' : 'p-3'}`} />
              )}
            </div>
            <div className={dense ? 'p-2.5' : 'p-3.5'}>
              {p.kind && <p className={`font-bold uppercase tracking-[0.05em] text-[#1d8efe] truncate ${dense ? 'text-[9.5px] mb-0.5' : 'text-[10.5px] mb-1'}`}>{p.kind}</p>}
              <p className={`text-[#1d1d1f] line-clamp-2 ${dense ? 'text-[11.5px] leading-[1.3] min-h-[30px]' : 'text-[13px] leading-[1.35] min-h-[35px]'}`}>{p.name}</p>
              {!dense && p.blurb && <p className="text-[11px] text-[#6b7078] leading-[1.35] mt-1 line-clamp-1">{p.blurb}</p>}
              <div className={`flex items-center justify-between ${dense ? 'mt-1.5' : 'mt-2'}`}>
                <div className="flex items-baseline gap-1">
                  {money(p.price)
                    ? <span className={`font-bold text-[#1d1d1f] ${dense ? 'text-[12.5px]' : 'text-[14px]'}`}>{money(p.price)}</span>
                    : <span className={`font-semibold text-[#9aa0a8] ${dense ? 'text-[11px]' : 'text-[12px]'}`}>Price: TBA</span>}
                  {!dense && p.save > 0 && money(p.list_price) && <span className="text-[11px] text-[#9aa0a8] line-through">{money(p.list_price)}</span>}
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1d8efe] ml-auto">
                  View<ExternalLink className="w-3 h-3" />
                </span>
              </div>
              {multi && p.supplier?.name && (
                <p className={`text-[#9aa0a8] truncate ${dense ? 'text-[9.5px] mt-1' : 'text-[10.5px] mt-1.5'}`}>from {p.supplier.name}</p>
              )}
            </div>
          </a>
        ))}
      </div>
      {/* See-more link(s) to the supplier profile(s) */}
      <div className={`flex flex-wrap gap-x-5 gap-y-1 ${dense ? 'mt-2.5' : 'mt-3'}`}>
        {suppliers.filter((s) => s.slug).map((s) => (
          <Link
            key={s.slug}
            to={`/suppliers/${s.slug}`}
            className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#1d8efe] hover:gap-1.5 transition-all"
            data-testid={`cyp-seemore-${need.need}-${s.slug}`}
          >
            See more from {s.name} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        ))}
      </div>
    </div>
  );
};

const CompleteYourProject = ({
  projectType,
  stage,
  buildStage,
  variant = 'full',
  limit,
  perNeed,
  dense = false,
  heading = "Products we've matched for this",
  intro = '',
  className = '',
  embedded = false,
  sideRail = false,
  railTopic = '',
  railSupplier = '',
  railMrecSupplier = '',
}) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!projectType && !stage && !buildStage) return;
    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams();
        if (projectType) qs.set('project_type', projectType);
        if (stage) qs.set('stage', stage);
        if (buildStage) qs.set('build_stage', buildStage);
        qs.set('limit', String(limit != null ? limit : (variant === 'compact' ? 1 : 3)));
        if (perNeed != null) qs.set('per_need', String(perNeed));
        const r = await fetch(`${API}/api/affiliate/recommendations?${qs.toString()}`);
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [projectType, stage, buildStage, variant, limit, perNeed]);

  // Internal-linking mesh: describe the strip's products as a schema.org
  // ItemList of Products (with real feed prices + Offers, never ratings).
  // The products are visibly rendered below, so the markup is representative.
  const productSchema = useMemo(() => {
    if (!data?.needs?.length) return [];
    const items = [];
    data.needs.forEach((need) => {
      (need.products || []).forEach((p) => {
        const n = parseFloat(String(p.price || '').replace(/[^0-9.]/g, ''));
        const sup = p.supplier?.name || need.suppliers?.[0]?.name || 'BuildPilot supplier';
        const supUrl = p.supplier?.slug ? `${SITE}/suppliers/${p.supplier.slug}` : SITE;
        items.push({
          '@type': 'ListItem',
          position: items.length + 1,
          item: {
            '@type': 'Product',
            name: p.name,
            ...(p.image ? { image: p.image } : {}),
            brand: { '@type': 'Brand', name: sup },
            ...(p.kind ? { category: p.kind } : {}),
            ...(Number.isFinite(n) ? {
              offers: {
                '@type': 'Offer',
                price: n,
                priceCurrency: p.currency || 'AUD',
                availability: 'https://schema.org/InStock',
                itemCondition: 'https://schema.org/NewCondition',
                url: supUrl,
                seller: { '@type': 'Organization', name: sup },
              },
            } : {}),
          },
        });
      });
    });
    if (!items.length) return [];
    return [{
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: heading || 'Recommended products',
      itemListElement: items,
    }];
  }, [data, heading]);
  useJsonLd(productSchema, `gear-strip:${projectType || stage || 'x'}`);

  if (!data || !data.needs || data.needs.length === 0) return null;

  const inner = (
    <>
      <div className={dense ? 'mb-4' : 'mb-6'}>
        <span className="text-[11px] font-bold uppercase tracking-[0.04em] text-[#1d8efe]">{heading}</span>
        {intro && <p className="text-[13.5px] text-[#6b7078] mt-1.5 max-w-[620px] leading-[1.5]">{intro}</p>}
      </div>
      {data.needs.map((need) => (
        <NeedRow key={need.need} need={need} dense={dense} />
      ))}
      <p className={`text-[12px] text-[#9aa0a8] leading-[1.5] max-w-[640px] ${dense ? 'mt-4' : 'mt-6'}`}>
        {data.disclosure}{' '}
        <Link to="/affiliate-disclosure" className="underline hover:text-[#1d1d1f]">Learn more</Link>.
      </p>
    </>
  );

  // Embedded mode: render bare (no full-bleed section chrome) so the strip can
  // live inside a column next to a side rail.
  if (embedded) {
    return (
      <div className={className} data-testid="complete-your-project">
        {inner}
      </div>
    );
  }

  // Side-rail mode: strip on the left, a sticky vertical supplier skyscraper on
  // the right (lg+ only). The rail's supplier is resolved by topic (or an
  // explicit railSupplier); PartnerBanner renders nothing when no vertical
  // creative matches, so the rail self-hides on pages with no relevant supplier.
  if (sideRail) {
    const topicForRail = railTopic || projectType || stage || '';
    return (
      <section className={`${dense ? 'py-8 sm:py-10' : 'py-12 sm:py-16'} bg-[#fafbfc] border-y border-gray-100 ${className}`} data-testid="complete-your-project">
        <div className="max-w-[1180px] mx-auto px-5 sm:px-6">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">
            <div className="flex-1 min-w-0 w-full">{inner}</div>
            <aside className="hidden lg:block shrink-0" data-testid="gear-side-rail">
              <div className="sticky top-24 space-y-4">
                {railMrecSupplier && (
                  <PartnerBanner size="medium_rectangle" supplier={railMrecSupplier} showCaption />
                )}
                <PartnerBanner size="wide_skyscraper" supplier={railSupplier} topic={topicForRail} showCaption />
              </div>
            </aside>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`${dense ? 'py-8 sm:py-10' : 'py-12 sm:py-16'} bg-[#fafbfc] border-y border-gray-100 ${className}`} data-testid="complete-your-project">
      <div className="max-w-[1080px] mx-auto px-5 sm:px-6">
        {inner}
      </div>
    </section>
  );
};

export default CompleteYourProject;
